import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ProductEntity, ProductStatus, SprintEntity } from '@data/utils/types';
import { fetchPmProducts } from '@data/api';
import * as queryKeys from '@data/constants/keys';
import { dayKey, fullDate } from '@app/utils/helpers/date-utils';
import { isAbsenceProduct } from '@app/components/shared/sprintDayReport/absenceProducts';

// Time per product across the year, read one sprint at a time from the windowed PM products endpoint.
// No endpoint is added: it already accepts from/to and already sums the minutes per product in SQL.

// Archived and deactivated products carry history too, and a year that dropped them would not reconcile
// with the reporting screens.
const ALL_STATUSES = [ProductStatus.active, ProductStatus.support, ProductStatus.archived, ProductStatus.deactivated];

/** Line colours, held as tokens so each keeps its step on the dark surface. */
export const PRODUCT_COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map(n => `var(--product-line-${n})`);
/** The colour of the segment that holds every product not picked. */
export const REST_COLOR = 'var(--product-rest)';
export const REST_KEY = 'rest';
/** Four products plus the rest; the palette is longer because colour follows rank in the whole list. */
export const MAX_LINES = 4;

/** One sprint as a window to ask for: half-open on the sprint after it, so no day is counted twice. */
export interface SprintWindow {
  id: string;
  number: number;
  /** Start of the sprint's first day. */
  from: string;
  /** The sprint's last day — the day before the next sprint starts. */
  to: string;
}

export interface ProductHours {
  id: string;
  name: string;
  billable: boolean;
  hours: number;
}

export interface ProductSeries {
  key: string;
  label: string;
  /** The colour this product takes when nothing else on the chart already has it. */
  color: string;
  /** Where the product sits in the full list, busiest first. What its colour follows. */
  rank: number;
  /** Hours reported in that sprint alone, aligned to the windows. */
  perSprint: number[];
  /** The whole window's hours. */
  total: number;
}

const sum = (values: number[]): number => values.reduce((total, v) => total + v, 0);
const atUtc = (iso: string): Date => new Date(`${iso}T00:00:00Z`);
const dayBefore = (iso: string): string => {
  const day = atUtc(iso);
  day.setUTCDate(day.getUTCDate() - 1);

  return day.toISOString().slice(0, 10);
};

/**
 * The sprints of `today`'s calendar year that have already started, oldest first, each as the window to ask
 * the endpoint for.
 *
 * The window ends on the day **before the next sprint starts** rather than on the sprint's own `endDate`.
 * Sprints are contiguous, and the two ways a sprint reaches the database disagree about what `endDate`
 * means — `generateSprints` leaves it at midnight of the last day, so a report filed that afternoon falls
 * outside it, while the seeder sets it to the next sprint's start, which counts the boundary day twice.
 * Bounding on the next start is right under both. See Decision 3 of the epic's architecture doc.
 */
export const yearSprintWindows = (sprints: SprintEntity[], today: Date): SprintWindow[] => {
  const byStart = [...sprints].sort((a, b) => fullDate(a.startDate).localeCompare(fullDate(b.startDate)));
  const todayIso = dayKey(today);
  const year = todayIso.slice(0, 4);

  return byStart
    .map((sprint, index) => {
      const from = fullDate(sprint.startDate);
      const next = byStart[index + 1];

      return {
        id: sprint.id,
        number: sprint.number,
        from,
        // The sprint running now has a successor only once the next one is generated. Until then its own
        // end is the best bound there is, and everything filed so far falls inside it either way.
        to: next ? dayBefore(fullDate(next.startDate)) : fullDate(sprint.endDate),
      };
    })
    .filter(w => w.from.slice(0, 4) === year && w.from <= todayIso);
};

export const toProductHours = (items?: ProductEntity[]): ProductHours[] =>
  (items ?? []).map(p => ({
    id: p.id,
    name: p.name ?? '—',
    // Absent reads as not billable: the trend is about billable work, and a product whose billable flag
    // never came back is not evidence that it is.
    billable: p.billable ?? false,
    hours: (p.totalMinutes ?? 0) / 60,
  }));

/**
 * Hours per product for each window, aligned to `windows`. One request per window — each caches on its own,
 * so changing the product selection costs nothing.
 */
export const useProductHoursBySprint = (
  organizationId: string | undefined,
  windows: SprintWindow[],
): { bySprint: ProductHours[][]; isLoading: boolean; isError: boolean } => {
  const queries = useQueries({
    queries: windows.map(w => ({
      queryKey: [queryKeys.PM_PRODUCTS, { organizationId, trend: w.id }],
      queryFn: () =>
        fetchPmProducts(organizationId!, {
          statuses: ALL_STATUSES,
          from: `${w.from}T00:00:00.000Z`,
          to: `${w.to}T00:00:00.000Z`,
        }),
      enabled: !!organizationId,
      staleTime: 60_000,
    })),
  });

  // The query objects are new on every render, so the memo keys on when the data last changed instead.
  const stamps = queries.map(q => q.dataUpdatedAt).join(',');
  const bySprint = useMemo(
    () => windows.map((_, i) => toProductHours(queries[i]?.data?.items)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windows, stamps],
  );

  return { bySprint, isLoading: queries.some(q => q.isLoading), isError: queries.some(q => q.isError) };
};

/** One series per billable product, busiest first; colour follows that rank so it survives the selection changing. */
export const sprintSeries = (bySprint: ProductHours[][]): ProductSeries[] => {
  const perProduct = new Map<string, { label: string; perSprint: number[] }>();

  bySprint.forEach((products, index) => {
    products
      // Holiday and absence are products too; the name check keeps them out even if one is marked billable.
      .filter(p => p.billable && !isAbsenceProduct(p.name))
      .forEach(p => {
        const entry = perProduct.get(p.id) ?? { label: p.name, perSprint: Array(bySprint.length).fill(0) };
        entry.perSprint[index] = p.hours;
        perProduct.set(p.id, entry);
      });
  });

  return (
    [...perProduct.entries()]
      .map(([key, { label, perSprint }]) => ({ key, label, perSprint, total: sum(perSprint) }))
      // A product with nothing logged all year is not offered at all.
      .filter(s => s.total > 0)
      .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
      .map((s, index) => ({ ...s, rank: index, color: PRODUCT_COLORS[index % PRODUCT_COLORS.length] }))
  );
};

/** The selected products, each moved off its rank colour only when a wrapped rank already holds it. */
export const withDistinctColors = (selected: ProductSeries[]): ProductSeries[] => {
  const taken = new Set<string>();

  return selected.map(series => {
    const color = taken.has(series.color) ? (PRODUCT_COLORS.find(c => !taken.has(c)) ?? series.color) : series.color;
    taken.add(color);

    return { ...series, color };
  });
};

/** Every product not picked, summed per sprint, so each bar still adds up to the whole company. */
export const restSeries = (all: ProductSeries[], selectedKeys: string[], label: string): ProductSeries | null => {
  const rest = all.filter(s => !selectedKeys.includes(s.key));
  if (!rest.length) return null;
  const perSprint = rest[0].perSprint.map((_, i) => rest.reduce((sum, s) => sum + (s.perSprint[i] ?? 0), 0));

  return { key: REST_KEY, label, color: REST_COLOR, rank: all.length, perSprint, total: sum(perSprint) };
};

/** Compact hours — "1.2Kh" for the axis and the legend, where a year's total has to fit a narrow slot. */
export const hoursCompact = (hours: number): string =>
  `${new Intl.NumberFormat('en-IE', {
    notation: 'compact',
    maximumFractionDigits: hours < 1000 ? 0 : 1,
  }).format(hours)}h`;

/** Round a value up to a whole axis bound: 410 → 500, 1_151 → 2_000. */
export const roundUpAxis = (value: number): number => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));

  return Math.ceil(value / magnitude) * magnitude;
};
